import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createLocalVideoTrack } from 'twilio-video';
import {
    applyBackgroundBlurToTrack,
    isBackgroundBlurEnabled,
    isBackgroundBlurSupported,
    removeBackgroundBlurFromTrack,
    setBackgroundBlurEnabled
} from '../../utils/backgroundBlur';
import './CameraSettings.css';

// Same keys the waiting room and video call read, so choices made here carry into calls.
const VIDEO_PREF_KEY = 'preferredVideoDeviceId';
const AUDIO_PREF_KEY = 'preferredAudioDeviceId';
const MIC_HEARD_THRESHOLD = 8;

const readPref = (key) => {
    try { return localStorage.getItem(key) || ''; } catch { return ''; }
};

const writePref = (key, value) => {
    try {
        if (value) localStorage.setItem(key, value);
        else localStorage.removeItem(key);
    } catch { /* storage blocked; the call falls back to the default device */ }
};

const isMissingDeviceError = (err) =>
    ['OverconstrainedError', 'NotFoundError', 'NotReadableError'].includes(err?.name);

const describeCameraError = (err) => {
    switch (err?.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'Camera access is blocked. Allow camera access for this site in your browser settings, then choose Start camera test again.';
        case 'NotFoundError':
        case 'OverconstrainedError':
            return 'No camera was found. Connect a camera, then choose Start camera test again.';
        case 'NotReadableError':
            return 'Your camera is being used by another app or browser tab. Close it there, then choose Start camera test again.';
        default:
            return 'We could not start your camera. Check that it is connected, then choose Start camera test again.';
    }
};

export default function CameraSettings() {
    const [videoInputs, setVideoInputs] = useState([]);
    const [audioInputs, setAudioInputs] = useState([]);
    const [selectedVideoId, setSelectedVideoId] = useState(() => readPref(VIDEO_PREF_KEY));
    const [selectedAudioId, setSelectedAudioId] = useState(() => readPref(AUDIO_PREF_KEY));
    const [blurEnabled, setBlurEnabled] = useState(() => isBackgroundBlurEnabled());
    const [blurSupported] = useState(() => isBackgroundBlurSupported());
    const [testing, setTesting] = useState(false);
    const [cameraStatus, setCameraStatus] = useState('idle'); // idle | starting | on | error
    const [blurStatus, setBlurStatus] = useState('idle'); // idle | loading | on | error
    const [micStatus, setMicStatus] = useState('idle'); // idle | listening | heard | error
    const [micLevel, setMicLevel] = useState(0);
    const [error, setError] = useState('');
    const [announcement, setAnnouncement] = useState('');

    const videoRef = useRef(null);
    const videoTrackRef = useRef(null);
    const audioStreamRef = useRef(null);
    const audioContextRef = useRef(null);
    const rafRef = useRef(null);
    const micHeardRef = useRef(false);
    // Bumped on every start/stop so a slow start that finishes after Stop
    // (or after leaving the page) releases its devices instead of keeping them.
    const runRef = useRef(0);

    const announce = useCallback((message) => {
        // Clear first so the same message twice in a row is still read out.
        setAnnouncement('');
        requestAnimationFrame(() => setAnnouncement(message));
    }, []);

    const loadDevices = useCallback(async () => {
        if (!navigator.mediaDevices?.enumerateDevices) return;
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videos = devices.filter((d) => d.kind === 'videoinput' && d.deviceId);
            const audios = devices.filter((d) => d.kind === 'audioinput' && d.deviceId);
            setVideoInputs(videos);
            setAudioInputs(audios);
            setSelectedVideoId((prev) => (videos.some((d) => d.deviceId === prev) ? prev : (videos[0]?.deviceId || '')));
            setSelectedAudioId((prev) => (audios.some((d) => d.deviceId === prev) ? prev : (audios[0]?.deviceId || '')));
        } catch (err) {
            console.warn('Unable to list media devices:', err);
        }
    }, []);

    const stopMic = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach((t) => t.stop());
            audioStreamRef.current = null;
        }
        micHeardRef.current = false;
        setMicLevel(0);
    }, []);

    const stopCamera = useCallback(() => {
        const track = videoTrackRef.current;
        if (track) {
            try {
                removeBackgroundBlurFromTrack(track);
                track.detach();
                track.stop();
            } catch (err) {
                console.warn('Error stopping camera preview:', err);
            }
            videoTrackRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
    }, []);

    const stopAll = useCallback(() => {
        runRef.current += 1;
        stopCamera();
        stopMic();
    }, [stopCamera, stopMic]);

    useEffect(() => {
        loadDevices();
        const md = navigator.mediaDevices;
        if (md?.addEventListener) md.addEventListener('devicechange', loadDevices);
        return () => {
            if (md?.removeEventListener) md.removeEventListener('devicechange', loadDevices);
            stopAll();
        };
    }, [loadDevices, stopAll]);

    const applyBlur = useCallback(async (track, enabled) => {
        if (!enabled || !blurSupported) {
            removeBackgroundBlurFromTrack(track);
            setBlurStatus('idle');
            return;
        }
        setBlurStatus('loading');
        const applied = await applyBackgroundBlurToTrack(track, { force: true });
        if (videoTrackRef.current !== track) return;
        setBlurStatus(applied ? 'on' : 'error');
    }, [blurSupported]);

    const startCamera = useCallback(async (deviceId, run) => {
        setCameraStatus('starting');
        const baseOptions = { frameRate: { ideal: 24, max: 30 }, facingMode: 'user' };
        let track;
        try {
            track = await createLocalVideoTrack(deviceId ? { ...baseOptions, deviceId: { exact: deviceId } } : baseOptions);
        } catch (err) {
            if (!deviceId || !isMissingDeviceError(err)) throw err;
            // Saved camera is gone (unplugged, new browser profile); fall back to the default one.
            writePref(VIDEO_PREF_KEY, '');
            track = await createLocalVideoTrack(baseOptions);
        }
        if (runRef.current !== run) {
            track.stop();
            return false;
        }
        videoTrackRef.current = track;
        if (videoRef.current) {
            track.attach(videoRef.current);
            videoRef.current.play().catch(() => {});
        }
        setCameraStatus('on');
        // Read the saved value, not state, in case blur was toggled while the camera was starting.
        await applyBlur(track, isBackgroundBlurEnabled());
        return true;
    }, [applyBlur]);

    const startMic = useCallback(async (deviceId, run) => {
        setMicStatus('listening');
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
        } catch (err) {
            if (!deviceId || !isMissingDeviceError(err)) throw err;
            writePref(AUDIO_PREF_KEY, '');
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        if (runRef.current !== run) {
            stream.getTracks().forEach((t) => t.stop());
            return false;
        }
        audioStreamRef.current = stream;

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        audioContextRef.current = ctx;
        if (ctx.state === 'suspended') await ctx.resume();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.3;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const data = new Uint8Array(analyser.fftSize);

        const tick = () => {
            analyser.getByteTimeDomainData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) {
                const sample = (data[i] - 128) / 128;
                sum += sample * sample;
            }
            const db = 20 * Math.log10(Math.sqrt(sum / data.length) + 0.0001);
            const level = Math.round(Math.pow(Math.max(0, Math.min(1, (db + 70) / 60)), 1.5) * 100);
            setMicLevel(level);
            if (level >= MIC_HEARD_THRESHOLD && !micHeardRef.current) {
                micHeardRef.current = true;
                setMicStatus('heard');
                announce('Your microphone is picking up sound.');
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        tick();
        return true;
    }, [announce]);

    const startTest = useCallback(async (videoId = selectedVideoId, audioId = selectedAudioId) => {
        stopAll();
        const run = runRef.current;
        setError('');
        setTesting(true);
        setBlurStatus('idle');
        setMicStatus('idle');

        if (!navigator.mediaDevices?.getUserMedia) {
            setTesting(false);
            setCameraStatus('error');
            setError('This browser cannot use a camera. Try the latest Chrome, Edge, Firefox or Safari.');
            return;
        }

        let cameraOk = false;
        try {
            cameraOk = await startCamera(videoId, run);
        } catch (err) {
            if (runRef.current !== run) return;
            console.error('Camera test failed:', err);
            setCameraStatus('error');
            setError(describeCameraError(err));
        }

        let micOk = false;
        try {
            micOk = await startMic(audioId, run);
        } catch (err) {
            if (runRef.current !== run) return;
            console.error('Microphone test failed:', err);
            setMicStatus('error');
        }

        if (runRef.current !== run) return;
        // Labels are only readable once permission is granted, so list again.
        loadDevices();
        if (cameraOk) {
            announce('Camera test started. You should see yourself in the preview.');
        } else if (!micOk) {
            setTesting(false);
        }
    }, [selectedVideoId, selectedAudioId, stopAll, startCamera, startMic, loadDevices, announce]);

    const stopTest = () => {
        stopAll();
        setTesting(false);
        setCameraStatus('idle');
        setBlurStatus('idle');
        setMicStatus('idle');
        announce('Camera test stopped. Your camera is off.');
    };

    const handleCameraChange = (e) => {
        const id = e.target.value;
        setSelectedVideoId(id);
        writePref(VIDEO_PREF_KEY, id);
        if (testing) startTest(id, selectedAudioId);
    };

    const handleMicChange = (e) => {
        const id = e.target.value;
        setSelectedAudioId(id);
        writePref(AUDIO_PREF_KEY, id);
        if (testing) startTest(selectedVideoId, id);
    };

    const handleBlurChange = async (e) => {
        const next = e.target.checked;
        setBlurEnabled(next);
        setBackgroundBlurEnabled(next);
        announce(next ? 'Background blur is on and saved.' : 'Background blur is off and saved.');
        const track = videoTrackRef.current;
        if (track) {
            await applyBlur(track, next);
            // Re-attach so the preview switches between the blurred and plain feed.
            if (videoRef.current && videoTrackRef.current === track) {
                track.detach(videoRef.current);
                track.attach(videoRef.current);
                videoRef.current.play().catch(() => {});
            }
        }
    };

    const cameraStatusText = {
        idle: 'Camera is off.',
        starting: 'Starting your camera…',
        on: 'Camera is working. You should see yourself in the preview.',
        error: 'Camera is not working.'
    }[cameraStatus];

    const blurStatusText = !blurSupported
        ? 'Background blur is not available in this browser. Try the latest Chrome or Edge.'
        : blurEnabled && testing && cameraStatus === 'on'
            ? {
                idle: '',
                loading: 'Applying blur to your preview…',
                on: 'Your background should look blurred in the preview.',
                error: 'Blur could not be applied here. Your camera still works without it.'
            }[blurStatus]
            : '';

    const micStatusText = {
        idle: '',
        listening: 'Say something to check your microphone.',
        heard: 'Your microphone is picking up sound.',
        error: 'We could not use your microphone. Check it is connected and that this site is allowed to use it.'
    }[micStatus];

    const deviceLabel = (device, index, kind) => device.label || `${kind} ${index + 1}`;

    return (
        <div className="dashboard-content camera-settings">
            <h1>Camera Settings</h1>
            <p className="camera-settings-intro">
                Check your camera and microphone before a video call, and choose whether to blur your background.
                Your choices are saved on this device and used in your next call.
            </p>

            <div className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</div>

            {error && (
                <div className="camera-settings-error" role="alert">
                    <p>{error}</p>
                    <button type="button" className="camera-settings-link-button" onClick={() => setError('')}>
                        Dismiss message
                    </button>
                </div>
            )}

            <div className="camera-settings-grid">
                <section className="camera-settings-card" aria-labelledby="camera-preview-heading">
                    <h2 id="camera-preview-heading">Camera preview</h2>
                    <div className="camera-settings-video-frame">
                        <video
                            ref={videoRef}
                            className={`camera-settings-video ${cameraStatus === 'on' ? 'is-live' : ''}`}
                            aria-label="Your camera preview"
                            autoPlay
                            muted
                            playsInline
                        />
                        {cameraStatus !== 'on' && (
                            <p className="camera-settings-video-placeholder" aria-hidden="true">
                                {cameraStatus === 'starting' ? 'Starting camera…' : 'Camera is off'}
                            </p>
                        )}
                    </div>
                    <p className={`camera-settings-status is-${cameraStatus}`}>{cameraStatusText}</p>
                    <button
                        type="button"
                        className="dashboard-button camera-settings-primary"
                        onClick={testing ? stopTest : () => startTest()}
                        disabled={cameraStatus === 'starting'}
                    >
                        {testing ? 'Stop camera test' : 'Start camera test'}
                    </button>
                </section>

                <section className="camera-settings-card" aria-labelledby="camera-options-heading">
                    <h2 id="camera-options-heading">Devices and background</h2>

                    <div className="camera-settings-field">
                        <label htmlFor="camera-settings-camera">Camera</label>
                        <select
                            id="camera-settings-camera"
                            value={selectedVideoId}
                            onChange={handleCameraChange}
                            disabled={videoInputs.length === 0 || cameraStatus === 'starting'}
                        >
                            {videoInputs.length === 0 && <option value="">No camera found yet</option>}
                            {videoInputs.map((d, i) => (
                                <option key={d.deviceId} value={d.deviceId}>{deviceLabel(d, i, 'Camera')}</option>
                            ))}
                        </select>
                    </div>

                    <div className="camera-settings-field">
                        <label htmlFor="camera-settings-mic">Microphone</label>
                        <select
                            id="camera-settings-mic"
                            value={selectedAudioId}
                            onChange={handleMicChange}
                            disabled={audioInputs.length === 0 || cameraStatus === 'starting'}
                        >
                            {audioInputs.length === 0 && <option value="">No microphone found yet</option>}
                            {audioInputs.map((d, i) => (
                                <option key={d.deviceId} value={d.deviceId}>{deviceLabel(d, i, 'Microphone')}</option>
                            ))}
                        </select>
                    </div>
                    {(videoInputs.length === 0 || videoInputs.every((d) => !d.label)) && (
                        <p className="camera-settings-help">
                            Device names appear after you choose Start camera test and allow access.
                        </p>
                    )}

                    <div className="camera-settings-field camera-settings-blur">
                        <label className="camera-settings-checkbox" htmlFor="camera-settings-blur">
                            <input
                                id="camera-settings-blur"
                                type="checkbox"
                                checked={blurEnabled && blurSupported}
                                disabled={!blurSupported}
                                onChange={handleBlurChange}
                                aria-describedby="camera-settings-blur-help"
                            />
                            <span>Blur my background in video calls</span>
                        </label>
                        <p id="camera-settings-blur-help" className="camera-settings-help">
                            Recruiters see you clearly while the room behind you is blurred. Start the camera test to see how it looks.
                        </p>
                        {blurStatusText && <p className="camera-settings-status">{blurStatusText}</p>}
                    </div>

                    {testing && micStatus !== 'idle' && (
                        <div className="camera-settings-field">
                            <p className="camera-settings-label">Microphone level</p>
                            {micStatus !== 'error' && (
                                <div className="camera-settings-meter" aria-hidden="true">
                                    <div className="camera-settings-meter-fill" style={{ width: `${Math.max(micLevel, 1)}%` }} />
                                </div>
                            )}
                            <p className={`camera-settings-status ${micStatus === 'error' ? 'is-error' : ''}`}>{micStatusText}</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
